// Features 10/16, Browse lane (R-94, alvin's pin 565): words that narrow Browse. Include = only show results with one of
// these; Exclude = hide results with any of these. Kept on this device (no account needed, no database), sent with every
// Browse and Ask request; the server matches title, venue and source (supabase/functions/_shared/browse_filters.ts).
// "Hide this kind" on a listing's detail adds its venue (or its source) to Exclude, with an Undo.
import { useSyncExternalStore } from 'react'
import { cleanWord, MAX_WORDS, type Words } from './wordMatch'
export { cleanWord, keepsWords, MAX_WORDS, WORD_MAX_LEN, type Words } from './wordMatch'

const KEY = 'mg_words'
const EMPTY: Words = { inc: [], exc: [] }

const cleanList = (x: unknown): string[] => {
  const out: string[] = []
  for (const v of Array.isArray(x) ? x : []) {
    const w = typeof v === 'string' ? cleanWord(v) : ''
    if (w.length >= 2 && !out.includes(w)) out.push(w)
  }
  return out.slice(0, MAX_WORDS)
}

function load(): Words {
  try {
    const j = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Words> | null
    return j ? { inc: cleanList(j.inc), exc: cleanList(j.exc) } : EMPTY
  } catch {
    return EMPTY
  }
}
let state: Words = typeof window === 'undefined' ? EMPTY : load()
const subs = new Set<() => void>()
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }

export const wordsNow = (): Words => state
export const useWords = (): Words => useSyncExternalStore(subscribe, wordsNow, () => EMPTY)

const same = (a: string[], b: string[]) => a.length === b.length && a.every((w, i) => w === b[i])
/** Save both lists (cleaned); a change tells every Browse view to ask again. */
export function setWords(next: Partial<Words>) {
  const w: Words = { inc: cleanList(next.inc ?? state.inc), exc: cleanList(next.exc ?? state.exc) }
  // a word in both lists only hides (exclude wins on the server too): keep it in one place
  w.inc = w.inc.filter((x) => !w.exc.includes(x))
  if (same(w.inc, state.inc) && same(w.exc, state.exc)) return
  state = w
  try { localStorage.setItem(KEY, JSON.stringify(w)) } catch { /* private mode: this visit only */ }
  subs.forEach((f) => f())
}

/** "Hide this kind": the venue when there is one, else the source (`word` as matched, `label` as the listing names it).
 *  null when the listing has neither. */
// a site that lists everything (or a public map) is not a kind: hiding it would hide half of Browse (B-158: never show a
// data source's name as if it were the place)
const BROAD = /\b(overture|openstreetmap|osm|wikidata|wikimedia|wikipedia|google|yelp|eventbrite|ticketmaster|meetup|partiful|luma|lu ma|dice|resident advisor|allevents|facebook|instagram)\b/
const VAGUE = /^(online|virtual|tba|tbd|various( locations)?|multiple locations|location tba|see (site|website)|los angeles|la)$/
export function kindOf(item: { venue_name?: string | null; source_name?: string | null }): { word: string; label: string } | null {
  for (const v of [item.venue_name, item.source_name]) {
    const w = cleanWord(v ?? '')
    if ((v === item.source_name && BROAD.test(w)) || VAGUE.test(w)) continue
    if (w.length >= 2) return { word: w, label: (v ?? '').trim().slice(0, 60) }
  }
  return null
}
/** Adds to the end; a full list lets its oldest word go so the newest always counts. */
export function hideKind(word: string) { setWords({ exc: [...state.exc.filter((w) => w !== word), word].slice(-MAX_WORDS) }) }
export function unhideKind(word: string) { setWords({ exc: state.exc.filter((w) => w !== word) }) }
